/**
 * Feature flag treatments for the mt_discount_context flag.
 * Maps to Split.io treatments defined in each domain's feature config.
 */
export type PromotionContextTreatment =
  | 'off'
  | 'off-with-shadowing'
  | 'on-with-shadowing'
  | 'on';

export function isShadowing(treatment: PromotionContextTreatment): boolean {
  return (
    treatment === 'off-with-shadowing' || treatment === 'on-with-shadowing'
  );
}

export function isContextPrimary(
  treatment: PromotionContextTreatment
): boolean {
  return treatment === 'on' || treatment === 'on-with-shadowing';
}

export interface ShadowComparisonResult<T> {
  match: boolean;
  label: string;
  primary: T;
  shadow: T;
  diff: Record<string, { primary: unknown; shadow: unknown }>;
}

interface CompareShadowArgs<T> {
  label: string;
  primary: T;
  shadow: T;
  keys: ReadonlyArray<keyof T>;
}

/**
 * Compares two objects on specified keys using strict equality.
 * Only use with primitive-valued keys (string, number, boolean).
 * Object or array values will always report as mismatched due to reference comparison.
 */
export function compareShadow<T>({
  label,
  primary,
  shadow,
  keys,
}: CompareShadowArgs<T>): ShadowComparisonResult<T> {
  const diff: Record<string, { primary: unknown; shadow: unknown }> = {};

  for (const key of keys) {
    if (primary[key] !== shadow[key]) {
      diff[String(key)] = {
        primary: primary[key],
        shadow: shadow[key],
      };
    }
  }

  return {
    match: Object.keys(diff).length === 0,
    label,
    primary,
    shadow,
    diff,
  };
}
